/**
 * User Data Tracker Service
 * Tracks user identification data for Sentry analytics
 *
 * Tracks:
 * - egt.lobby.user_data.player_id: The player's unique identifier
 * - egt.lobby.user_data.user_session: The user's session identifier
 * - egt.lobby.user_data.user_settings.sound: 1 if sound is enabled, 0 otherwise
 * - egt.lobby.user_data.user_settings.language: The user's language code
 */

interface UserSettings {
    sound: number;
    language: string;
}

interface UserData {
    player_id: string;
    user_session: string;
    settings: UserSettings;
}

class UserDataTracker {
    private static instance: UserDataTracker;
    private userData: UserData;

    private constructor() {
        // Initialize with default values
        this.userData = {
            player_id: '',
            user_session: '',
            settings: {
                sound: 0,
                language: '',
            },
        };
    }

    public static getInstance(): UserDataTracker {
        if (!UserDataTracker.instance) {
            UserDataTracker.instance = new UserDataTracker();
        }
        return UserDataTracker.instance;
    }

    /**
     * Set user data (preserves existing settings)
     * @param playerId - The player's unique identifier
     * @param userSession - The user's session identifier
     */
    public setUserData(playerId: string, userSession: string): void {
        this.userData.player_id = playerId;
        this.userData.user_session = userSession;
    }

    /**
     * Set player ID only
     * @param playerId - The player's unique identifier
     */
    public setPlayerId(playerId: string): void {
        this.userData.player_id = playerId;
    }

    /**
     * Set user session only
     * @param userSession - The user's session identifier
     */
    public setUserSession(userSession: string): void {
        this.userData.user_session = userSession;
    }

    /**
     * Set sound setting
     * @param soundEnabled - Whether sound is enabled
     */
    public setSound(soundEnabled: boolean): void {
        this.userData.settings.sound = soundEnabled ? 1 : 0;
    }

    /**
     * Set language setting
     * @param language - The language code
     */
    public setLanguage(language: string): void {
        this.userData.settings.language = language;
    }

    /**
     * Set all settings at once
     * @param soundEnabled - Whether sound is enabled
     * @param language - The language code
     */
    public setSettings(soundEnabled: boolean, language: string): void {
        this.userData.settings.sound = soundEnabled ? 1 : 0;
        this.userData.settings.language = language;
    }

    /**
     * Get user data formatted for Sentry
     */
    public getFormattedUserData(): Record<string, string | number> {
        return {
            'egt.lobby.user_data.player_id': this.userData.player_id,
            'egt.lobby.user_data.user_session': this.userData.user_session,
            'egt.lobby.user_data.user_settings.sound': this.userData.settings.sound,
            'egt.lobby.user_data.user_settings.language': this.userData.settings.language,
        };
    }

    /**
     * Get the current user data (for debugging)
     */
    public getUserData(): UserData {
        return { ...this.userData };
    }

    /**
     * Clear user data (useful for logout or new sessions)
     */
    public clear(): void {
        this.userData = {
            player_id: '',
            user_session: '',
            settings: {
                sound: 0,
                language: '',
            },
        };
    }
}

// Export singleton instance
export const userDataTracker = UserDataTracker.getInstance();
